import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useMarkThreadRead, usePostMessage } from '../../mutations';
import { useMessages } from '../../queries';
import type { ThreadSummary } from '../../types';
import { Avatar } from '@/shared/ui/atoms/Avatar';
import { Button } from '@/shared/ui/atoms/Button';
import { Spinner } from '@/shared/ui/atoms/Spinner';
import { useToast } from '@/shared/ui/organisms/Toast/useToast';
import { formatRelativeTime } from '@/shared/utils/time';
import './ChatPane.styl';

type Props = { thread: ThreadSummary; memberCount?: number | null };

export function ChatPane({ thread, memberCount }: Props) {
  const { data, isPending } = useMessages(thread.id);
  const post = usePostMessage(thread.id);
  const markRead = useMarkThreadRead();
  const toast = useToast();
  const { t } = useTranslation(undefined, { keyPrefix: 'messages' });
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const markReadMutate = markRead.mutate;

  useEffect(() => {
    if (thread.unread > 0) markReadMutate(thread.id);
  }, [thread.id, thread.unread, markReadMutate]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [data?.items.length]);

  const send = () => {
    const body = draft.trim();
    if (!body || post.isPending) return;
    post.mutate(body, { onSuccess: () => setDraft(''), onError: () => toast.show(t('sendError'), 'error') });
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="f-chat">
      <header className="f-chat__head">
        <Avatar initials={thread.initials} color={thread.color} size={36} square />
        <div>
          <div className="f-chat__name">{thread.name}</div>
          <div className="f-chat__meta">
            {t(`kind.${thread.kind}`)}
            {memberCount ? ` · ${t('members', { count: memberCount })}` : ''}
          </div>
        </div>
      </header>
      <div className="f-chat__body" role="log" aria-label={t('logLabel', { name: thread.name })}>
        {isPending ? <Spinner label={t('loading')} /> : null}
        {data?.items.map((m) => (
          <div key={m.id} className={m.mine ? 'f-chat__msg f-chat__msg--mine' : 'f-chat__msg'}>
            <Avatar initials={m.authorInitials} color={m.mine ? '#0B1D3A' : '#E6EBF2'} size={30} dark={m.mine} />
            <div className="f-chat__bubble-wrap">
              <div className="f-chat__author">
                {m.mine ? t('you') : m.authorName} · {formatRelativeTime(m.sentAt)}
              </div>
              <div className="f-chat__bubble">{m.body}</div>
            </div>
          </div>
        ))}
        {data && data.items.length === 0 ? <div className="f-chat__empty">{t('start')}</div> : null}
        <div ref={endRef} />
      </div>
      <form
        className="f-chat__composer"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <textarea className="f-chat__input" rows={1} placeholder={thread.kind === 'CLIENT' ? t('placeholderClient') : t('placeholderChannel')} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} aria-label={t('input')} />
        <Button type="submit" loading={post.isPending} disabled={!draft.trim()}>
          {t('send')}
        </Button>
      </form>
    </div>
  );
}
